import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Typography, Container, Box } from "@mui/material";

const ROLE_MAP = {
  a: "admin",
  s: "supervisor",
  c: "cashier",
};

export default function ProtectedRoute({ children, roles = [] }) {
  const location = useLocation();

  // 🛡️ Safe Extraction Context
  const getSessionUser = () => {
    try {
      const storedUser = localStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (err) {
      console.error("🔒 Auth Session Parsing Failed:", err);
      return null;
    }
  };

  const user = getSessionUser();

  // 1. Unauthenticated -> Kick out to login, but save where they wanted to go
  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // 2. Normalize roles to support both short keys ("a") and full names ("admin")
  const rawRole = user.role?.toLowerCase();
  const mappedRole = ROLE_MAP[rawRole] || rawRole;

  // Check if user has permission using either variant
  const hasAccess = roles.length === 0 || 
                    roles.includes(rawRole) || 
                    roles.includes(mappedRole);

  // 3. Unauthorized -> Render clean, centered feedback instead of crashing layout
  if (!hasAccess) {
    return (
      <Container maxWidth="sm">
        <Box 
          sx={{ 
            mt: 12, 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center",
            textAlign: "center",
            p: 4,
            boxShadow: 2,
            borderRadius: 2,
            bgcolor: "background.paper"
          }}
        >
          <Typography variant="h3" sx={{ mb: 2 }}>🚫</Typography>
          <Typography variant="h5" color="error" fontWeight="bold" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your account role (<strong>{mappedRole.toUpperCase()}</strong>) does not have clearance to access this module.
          </Typography>
        </Box>
      </Container>
    );
  }

  // 4. Authorized -> Render layout cleanly
  return children;
}